import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { userSettingsService } from '../../services/userSettingsService';
import AppIcon from '../ui/AppIcon';
import { 
  FaUser, 
  FaLock, 
  FaCreditCard, 
  FaBell, 
  FaMoon, 
  FaPlay, 
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaQuestionCircle,
  FaEnvelope,
  FaShieldAlt,
  FaFileAlt
} from 'react-icons/fa';
import { 
  updatePassword, 
  deleteUser, 
  reauthenticateWithCredential, 
  EmailAuthProvider 
} from 'firebase/auth';

const SettingsPage = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Settings preferences
  const [settings, setSettings] = useState({
    notifications: true,
    darkMode: false,
    autoPlay: true,
    emailUpdates: true,
    progressReminders: false,
  });

  const [message, setMessage] = useState({ type: '', text: '' });

  // Load user settings on component mount and set up real-time sync
  useEffect(() => {
    if (!user) return;

    console.log('🔥 Setting up real-time settings sync for user:', user.uid);
    
    const unsubscribe = userSettingsService.subscribeToUserSettings(
      user.uid,
      (result) => {
        if (result.success) {
          setSettings(result.settings);
          console.log('⚙️ Settings synced from Firestore:', result.settings);
        } else {
          console.error('❌ Settings sync error:', result.error);
        }
      }
    );

    return () => {
      console.log('🔥 Cleaning up settings listener');
      unsubscribe();
    };
  }, [user]);

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordData.newPassword);
      
      setMessage({ type: 'success', text: 'Password updated successfully' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setShowPasswordSection(false);
    } catch (error) {
      console.error('Password update error:', error);
      let errorMessage = 'Failed to update password';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please logout and login again before changing password';
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      const password = prompt('Please enter your current password to confirm account deletion:');
      if (password) {
        deleteAccountWithPassword(password);
      }
    }
  };

  const deleteAccountWithPassword = async (password) => {
    setLoading(true);
    try {
      // Re-authenticate user before deleting account
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      
      // Delete user account
      await deleteUser(user);
      
      alert('Your account has been successfully deleted');
      // Navigation will be handled automatically by auth state change
    } catch (error) {
      console.error('Account deletion error:', error);
      let errorMessage = 'Failed to delete account';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Password is incorrect';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please logout and login again before deleting account';
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSettingChange = async (key, value) => {
    const newSettings = {
      ...settings,
      [key]: value
    };
    
    setSettings(newSettings);
    
    // Save to Firestore for real-time sync
    if (user) {
      const result = await userSettingsService.saveUserSettings(user.uid, newSettings);
      if (result.success) {
        setMessage({ type: 'success', text: 'Settings updated and synced across devices!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to sync settings. Please try again.' });
      }
    }
  };

  return (
    <div className="container">
      <div className="settings-page">
        <div className="settings-header">
          <AppIcon size={60} />
          <h1>Account Settings</h1>
          <p>Manage your account and preferences</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Account Section */}
        <div className="settings-section">
          <h2><FaUser /> Account Information</h2>
          
          <div className="account-info">
            <div className="user-profile">
              <div className="user-avatar">
                <FaUser size={40} />
              </div>
              <div className="user-details">
                <h3>{user?.email}</h3>
                <p className="user-status">
                  {userProfile?.subscriptionStatus === 'premium' ? '✨ Premium User' : 
                   userProfile?.subscriptionStatus === 'paid' ? '💎 Paid User' : '🆓 Free User'}
                </p>
                {userProfile?.subscriptionEndDate && (
                  <p className="subscription-info">
                    {userProfile.subscriptionStatus === 'premium' 
                      ? `Renews: ${new Date(userProfile.subscriptionEndDate).toLocaleDateString()}`
                      : `Expires: ${new Date(userProfile.subscriptionEndDate).toLocaleDateString()}`
                    }
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <FaLock />
              <div>
                <h3>Change Password</h3>
                <p>Update your account password</p>
              </div>
            </div>
            <button 
              className="btn btn-outline"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
            >
              {showPasswordSection ? 'Cancel' : 'Change Password'}
            </button>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <FaCreditCard />
              <div>
                <h3>Subscription Plans</h3>
                <p>Manage your subscription and billing</p>
              </div>
            </div>
            <a href="/subscription" className="btn btn-outline">
              Manage Subscription
            </a>
          </div>
        </div>

        {/* Password Change Section */}
        {showPasswordSection && (
          <div className="settings-section password-section">
            <h2>Change Password</h2>
            
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <div className="password-input-group">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter current password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({
                    ...passwordData,
                    currentPassword: e.target.value
                  })}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('current')}
                >
                  {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="password-input-group">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Enter new password (min 6 characters)"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({
                    ...passwordData,
                    newPassword: e.target.value
                  })}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('new')}
                >
                  {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <div className="password-input-group">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Confirm new password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({
                    ...passwordData,
                    confirmPassword: e.target.value
                  })}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handlePasswordChange}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Updating...
                </>
              ) : (
                <>
                  <FaCheck />
                  Update Password
                </>
              )}
            </button>
          </div>
        )}

        {/* Preferences Section */}
        <div className="settings-section">
          <h2><FaBell /> Preferences</h2>
          
          <div className="setting-item">
            <div className="setting-info">
              <FaBell />
              <div>
                <h3>Push Notifications</h3>
                <p>Receive notifications about new content and updates</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.notifications}
                onChange={(e) => handleSettingChange('notifications', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <FaMoon />
              <div>
                <h3>Dark Mode</h3>
                <p>Switch to dark theme for comfortable reading</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.darkMode}
                onChange={(e) => handleSettingChange('darkMode', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <FaPlay />
              <div>
                <h3>Auto-play Content</h3>
                <p>Automatically proceed to next chapter after completion</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.autoPlay}
                onChange={(e) => handleSettingChange('autoPlay', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <FaBell />
              <div>
                <h3>Email Updates</h3>
                <p>Receive weekly mindfulness tips and platform updates</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.emailUpdates}
                onChange={(e) => handleSettingChange('emailUpdates', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Support Section */}
        <div className="settings-section">
          <h2>Support</h2>
          
          <div className="support-links">
            <a href="/help" className="support-link">
              <FaQuestionCircle />
              <div>
                <h3>Help & FAQ</h3>
                <p>Find answers to common questions</p>
              </div>
            </a>

            <a href="/contact" className="support-link">
              <FaEnvelope />
              <div>
                <h3>Contact Support</h3>
                <p>Get help from our support team</p>
              </div>
            </a>

            <a href="/privacy" className="support-link">
              <FaShieldAlt />
              <div>
                <h3>Privacy Policy</h3>
                <p>Learn how we protect your data</p>
              </div>
            </a>

            <a href="/terms" className="support-link">
              <FaFileAlt />
              <div>
                <h3>Terms of Service</h3>
                <p>Read our terms and conditions</p>
              </div>
            </a>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="settings-section danger-section">
          <h2>Danger Zone</h2>
          
          <div className="danger-warning">
            <p>⚠️ These actions are permanent and cannot be undone.</p>
          </div>

          <div className="setting-item danger-item">
            <div className="setting-info">
              <FaTrash />
              <div>
                <h3>Delete Account</h3>
                <p>Permanently delete your account and all associated data</p>
              </div>
            </div>
            <button 
              className="btn btn-danger"
              onClick={handleDeleteAccount}
              disabled={loading}
            >
              Delete Account
            </button>
          </div>
        </div>

        {/* App Info */}
        <div className="app-info">
          <AppIcon size={40} />
          <div className="app-details">
            <h3>Nurturing Inner Love</h3>
            <p>Version 1.0.0</p>
            <p>Mindfulness & Self-Awareness Platform</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
